import { AdsProvider, RewardResult } from '../ads';
import { MONETIZATION } from '../config';
import { trackEvent } from '../events';
import { hideWebAdSlot, showWebAdSlot } from '../../ui/ad-slot';

interface H5AdBreakInfo {
  breakStatus?: string;
  breakName?: string;
}

interface H5AdsWindow {
  adBreak?: (options: Record<string, unknown>) => void;
  adConfig?: (options: Record<string, unknown>) => void;
}

function h5(): H5AdsWindow {
  return window as unknown as H5AdsWindow;
}

export class WebAdsProvider implements AdsProvider {
  private ready = false;
  private bannerVisible = false;

  async initialize(): Promise<void> {
    const client = MONETIZATION.ids.webAdClient;
    if (!client) {
      return;
    }
    const config = h5().adConfig;
    if (typeof config !== 'function') {
      return;
    }
    config({
      preloadAdBreaks: 'on',
      sound: 'off',
      onReady: () => {
        this.ready = true;
      },
    });
  }

  async showBanner(): Promise<void> {
    if (this.bannerVisible) {
      return;
    }
    const shown = await showWebAdSlot({
      client: MONETIZATION.ids.webAdClient,
      slot: MONETIZATION.ids.webBannerSlot,
    });
    if (shown) {
      this.bannerVisible = true;
      trackEvent('ad_banner_shown', { provider: 'web' });
    }
  }

  async hideBanner(): Promise<void> {
    if (!this.bannerVisible) {
      return;
    }
    hideWebAdSlot();
    this.bannerVisible = false;
    trackEvent('ad_banner_hidden', { provider: 'web' });
  }

  async showInterstitial(): Promise<boolean> {
    const result = await this.runAdBreak('next', 'game-over');
    return result === 'granted';
  }

  async prepareRewarded(): Promise<boolean> {
    return this.ready;
  }

  isRewardedReady(): boolean {
    return this.ready;
  }

  async showRewarded(): Promise<RewardResult> {
    return await this.runAdBreak('reward', 'continue');
  }

  private runAdBreak(type: 'next' | 'reward', name: string): Promise<RewardResult> {
    const adBreak = h5().adBreak;
    if (!this.ready || typeof adBreak !== 'function') {
      trackEvent('ad_rewarded_unavailable', { provider: 'web' });
      return Promise.resolve('unavailable');
    }
    trackEvent('ad_rewarded_requested', { provider: 'web', type });
    return new Promise<RewardResult>((resolve) => {
      let settled = false;
      let viewed = false;
      const finish = (result: RewardResult): void => {
        if (settled) {
          return;
        }
        settled = true;
        resolve(result);
      };
      try {
        adBreak({
          type,
          name,
          sound: 'off',
          beforeReward: (showAdFn: () => void) => showAdFn(),
          adViewed: () => {
            viewed = true;
          },
          adDismissed: () => {
            finish(viewed ? 'granted' : 'dismissed');
          },
          adBreakDone: (info: H5AdBreakInfo) => {
            const status = info?.breakStatus ?? 'unknown';
            if (status === 'viewed') {
              finish('granted');
              return;
            }
            if (status === 'dismissed') {
              finish('dismissed');
              return;
            }
            if (
              status === 'notReady' ||
              status === 'frequencyCapped' ||
              status === 'timeout' ||
              status === 'error' ||
              status === 'ignored'
            ) {
              finish('unavailable');
              return;
            }
            finish(viewed ? 'granted' : 'unavailable');
          },
        });
      } catch {
        finish('failed');
      }
    });
  }
}
